import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { authApi, consentApi } from '../api/client';
import { hasPendingConsents } from '../utils/consent';
import { useAdminScope } from '../hooks/useAdminScope';
import { showAdminPath } from '../utils/adminAccess';
import { MANUAL_TOP_URL } from '../utils/manualLinks';

/**
 * 管理者ナビの構造（2026-05-31 メニュー再編）
 *
 * 旧来の 9 件横並びはヘッダーを圧迫していたため、3 つの単独項目 + 3 つのグループに整理。
 * 各グループはドロップダウン（PC）/ インライン展開（スマホ）で表示する。
 */
const ADMIN_NAV_SINGLES = [
  { to: '/admin', end: true, labelKey: 'admin.nav.dashboard' },
  { to: '/admin/prompts', labelKey: 'admin.nav.prompts' },
  { to: '/admin/reports', labelKey: 'admin.nav.reports' },
  { to: '/admin/weekly-reports', labelKey: 'admin.nav.weeklyReports' },
] as const;

interface AdminNavGroupDef {
  id: 'users' | 'consent' | 'settings';
  labelKey: string;
  items: { to: string; labelKey: string }[];
}

const ADMIN_NAV_GROUPS: AdminNavGroupDef[] = [
  {
    id: 'users',
    labelKey: 'admin.nav.usersGroup',
    items: [
      { to: '/admin/users', labelKey: 'admin.nav.users' },
      { to: '/admin/users/import', labelKey: 'admin.nav.usersImport' },
      { to: '/admin/users/bulk-department', labelKey: 'admin.nav.usersBulkDepartment' },
      { to: '/admin/departments', labelKey: 'admin.nav.departments' },
    ],
  },
  {
    id: 'consent',
    labelKey: 'admin.nav.consentGroup',
    items: [
      { to: '/admin/consent', labelKey: 'admin.nav.consent' },
      { to: '/admin/data-requests', labelKey: 'admin.nav.dataRequests' },
    ],
  },
  {
    id: 'settings',
    labelKey: 'admin.nav.settingsGroup',
    items: [
      { to: '/admin/settings', labelKey: 'admin.nav.settings' },
      { to: '/admin/organization-mode', labelKey: 'admin.nav.orgMode' },
      { to: '/admin/audit-logs', labelKey: 'admin.nav.auditLogs' },
    ],
  },
];

interface NavGroupProps {
  id: string;
  label: string;
  open: boolean;
  active: boolean;
  onToggle: () => void;
  children: ReactNode;
}

function NavGroup({ id, label, open, active, onToggle, children }: NavGroupProps) {
  return (
    <div className={`layout-nav-group${open ? ' open' : ''}${active ? ' active' : ''}`}>
      <button
        type="button"
        className="layout-nav-group-trigger"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={`nav-group-${id}-menu`}
      >
        <span>{label}</span>
        <span className="layout-nav-group-chevron" aria-hidden="true">
          ▾
        </span>
      </button>
      <div
        id={`nav-group-${id}-menu`}
        className="layout-nav-group-menu"
        role="menu"
        hidden={!open}
      >
        {children}
      </div>
    </div>
  );
}

export default function Layout() {
  const { t } = useTranslation();
  const { user, logout, getToken } = useAuth();
  const adminScope = useAdminScope();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [employeeConsentReady, setEmployeeConsentReady] = useState(() => user?.role !== 'employee');
  /** ログイン中のアカウント名（#174）。表示名が空ならメールアドレス。読めないときは何も出さない */
  const [accountName, setAccountName] = useState<string | null>(null);
  const navRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (user?.role !== 'employee') {
      setEmployeeConsentReady(true);
      return;
    }
    const path = location.pathname;
    if (path.startsWith('/dashboard/onboarding') || path.startsWith('/dashboard/settings/consent')) {
      setEmployeeConsentReady(true);
      return;
    }
    const token = getToken();
    if (!token) {
      setEmployeeConsentReady(true);
      return;
    }
    let cancelled = false;
    setEmployeeConsentReady(false);
    consentApi
      .list(token)
      .then((res) => {
        if (cancelled) return;
        const rows = res.data ?? [];
        if (hasPendingConsents(rows)) {
          navigate('/dashboard/onboarding', { replace: true });
        }
      })
      .catch(() => {
        /* 取得失敗時は利用をブロックしない */
      })
      .finally(() => {
        if (!cancelled) setEmployeeConsentReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.role, location.pathname, getToken, navigate]);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setAccountName(null);
      return;
    }
    let cancelled = false;
    authApi
      .me(token)
      .then((me) => {
        if (!cancelled) setAccountName(me.display_name || me.email || null);
      })
      .catch(() => {
        if (!cancelled) setAccountName(null);
      });
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  // パス遷移でドロップダウンとハンバーガーを閉じる
  useEffect(() => {
    setOpenGroup(null);
    setMenuOpen(false);
  }, [location.pathname]);

  // 画面外クリック / Escape でドロップダウンを閉じる
  useEffect(() => {
    if (!openGroup) return;
    const onPointer = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenGroup(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenGroup(null);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [openGroup]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const closeAll = () => {
    setMenuOpen(false);
    setOpenGroup(null);
  };

  const isGroupActive = (group: AdminNavGroupDef) =>
    group.items.some((item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`));

  return (
    <div className="layout">
      <header className={`layout-header ${menuOpen ? 'layout-nav-open' : ''}`}>
        <span className="layout-brand">{t('app.name')}</span>
        <button
          type="button"
          className="layout-hamburger"
          onClick={() => setMenuOpen((o) => !o)}
          aria-expanded={menuOpen}
          aria-label={menuOpen ? 'メニューを閉じる' : 'メニューを開く'}
        >
          <span /><span /><span />
        </button>
        <nav className="layout-nav" ref={navRef}>
          {user?.role === 'employee' && (
            <>
              <NavLink to="/dashboard" className={({ isActive }) => (isActive ? 'active' : '')} end onClick={closeAll}>
                {t('employee.dashboard')}
              </NavLink>
              <NavLink to="/dashboard/history" className={({ isActive }) => (isActive ? 'active' : '')} onClick={closeAll}>
                {t('employee.history')}
              </NavLink>
              <NavLink to="/dashboard/notifications" className={({ isActive }) => (isActive ? 'active' : '')} onClick={closeAll}>
                {t('employee.notifications')}
              </NavLink>
              <NavLink to="/dashboard/settings" className={({ isActive }) => (isActive ? 'active' : '')} onClick={closeAll}>
                {t('employee.settings')}
              </NavLink>
              {/* 使い方ページはアプリの外のサイト（#101）。画面を離れないよう新しいタブで開く */}
              <a
                href={MANUAL_TOP_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('employee.manualAriaLabel')}
                onClick={closeAll}
              >
                {t('employee.manual')}
                <span className="layout-nav-external" aria-hidden="true">↗</span>
              </a>
            </>
          )}
          {user?.role === 'admin' && (
            <>
              {/* 拠点管理者には、全社管理者専用の画面（レポート・監査ログ等）を出さない（#100） */}
              {ADMIN_NAV_SINGLES.filter((item) => showAdminPath(item.to, adminScope)).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={'end' in item ? item.end : undefined}
                  className={({ isActive }) => (isActive ? 'active' : '')}
                  onClick={closeAll}
                >
                  {t(item.labelKey)}
                </NavLink>
              ))}
              {ADMIN_NAV_GROUPS.map((group) => ({
                ...group,
                items: group.items.filter((item) => showAdminPath(item.to, adminScope)),
              }))
                .filter((group) => group.items.length > 0)
                .map((group) => (
                <NavGroup
                  key={group.id}
                  id={group.id}
                  label={t(group.labelKey)}
                  open={openGroup === group.id}
                  active={isGroupActive(group)}
                  onToggle={() => setOpenGroup((prev) => (prev === group.id ? null : group.id))}
                >
                  {group.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) => (isActive ? 'active' : '')}
                      onClick={closeAll}
                      role="menuitem"
                    >
                      {t(item.labelKey)}
                    </NavLink>
                  ))}
                </NavGroup>
              ))}
            </>
          )}
          {user?.role === 'healthcare' && (
            <NavLink to="/healthcare" className={({ isActive }) => (isActive ? 'active' : '')} onClick={closeAll}>
              {t('healthcare.candidates')}
            </NavLink>
          )}
          {accountName && (
            <span
              className="layout-account"
              title={accountName}
              aria-label={t('common.loggedInAs', { name: accountName })}
              data-testid="layout-account"
            >
              {accountName}
            </span>
          )}
          <button type="button" className="layout-logout layout-nav-logout" onClick={() => { closeAll(); handleLogout(); }}>
            {t('common.logout')}
          </button>
        </nav>
      </header>
      <main className="layout-main">
        {user?.role === 'employee' && !employeeConsentReady ? (
          <div className="loading-screen">{t('common.loading')}</div>
        ) : (
          <Outlet />
        )}
      </main>
    </div>
  );
}
